import {
  LOAD_CART,
  ADD_PRODUCT,
  REMOVE_PRODUCT,
  DELIVERY_DATE,
  DELIVERY_TIME_SLOT,
  // Phase 2 actions
  LOAD_PHASE2_CART,
  ADD_PHASE2_PRODUCT,
  REMOVE_PHASE2_PRODUCT,
  UPDATE_PHASE2_CART,
  CLEAR_PHASE2_CART,
  // Error actions
  CART_STORE_LIMITATION_ERROR,
  CLEAR_CART_ERROR,
} from "./actionTypes";
import { UPDATE_CART_ITEMS, GET_CART_ITEMS_URL } from "../../configs";
import { isItemCompatibleWithCart, getCurrentCartType } from "../../helpers/cartMode";
import { areCartAddonsEqual } from "../../helpers/cartAddons";
import { clearCartStorage } from "../../helpers/clearCartStorage";
import { CartType } from "../../constants";
import { updateCart } from "../total/actions";
import Axios from "axios";

/** Full cart line for /api/save-cart (keeps size/variant selections). */
const serializeCartItemForApi = (eachItem) => {
  const restaurantId =
    eachItem.restaurant_id ||
    (eachItem.restaurant && eachItem.restaurant.id) ||
    null;
  if (
    !eachItem.id ||
    !restaurantId ||
    !eachItem.name ||
    eachItem.price == null ||
    !eachItem.quantity
  ) {
    return null;
  }

  const payload = {
    id: eachItem.id,
    restaurant_id: restaurantId,
    item_category_id: eachItem.item_category_id,
    name: eachItem.name,
    price: eachItem.price,
    old_price: eachItem.old_price,
    quantity: eachItem.quantity,
    zone_id: eachItem.zone_id || null,
    image: eachItem.image || null,
  };

  if (eachItem.selectedaddons && eachItem.selectedaddons.length > 0) {
    payload.selectedaddons = eachItem.selectedaddons.map((a) => ({
      id: a.id || a.addon_id,
      addon_id: a.addon_id || a.id,
      addon_category_id: a.addon_category_id || a.category_id,
      price: a.price,
      addon_category_name: a.addon_category_name,
      addon_name: a.addon_name,
    }));
  }

  return payload;
};

// Debouncing mechanism for API calls
let phase1ApiCallTimeout = null;
let phase2ApiCallTimeout = null;
const API_DEBOUNCE_DELAY = 300; // 300ms delay

// Utility function to handle cart API calls
const makeCartApiCall = async (payload, userToken) => {
  try {
    //   url: UPDATE_CART_ITEMS,
    //   payload,
    //   hasToken: !!userToken
    // });

    const response = await Axios.post(UPDATE_CART_ITEMS, payload, {
      headers: {
        'Authorization': `Bearer ${userToken}`,
        'Content-Type': 'application/json',
      }
    });


    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error('❌ Cart API error:', error.response && error.response.data ? error.response.data : error.message);

    return {
      success: false,
      error: error.response && error.response.data ? error.response.data : { message: 'Network error occurred' }
    };
  }
};

// Utility function to handle cart success/error responses
const handleCartResponse = (result, cartType) => {
  if (result.success) {
    const { data } = result;

    // Show success notification if needed
    if (data.items_count > 0) {
    }

    return data;
  } else {
    const { error } = result;
    console.error(`❌ ${cartType} Cart Error:`, error);

    // Handle network errors gracefully - don't crash the app
    if (error.message === 'Network error occurred' || error.message === 'Network Error') {
      console.warn(`🌐 Network error in ${cartType} cart - operating in offline mode`);
      // Return a graceful fallback that allows the app to continue
      return {
        success: true,
        offline: true,
        message: 'Cart saved locally - will sync when connection is restored',
        cart_type: cartType.toLowerCase()
      };
    }

    if (error.error && error.message) {
      // Handle Sprint cart store limitation error
      if (error.error === "Sprint cart store limitation") {
        console.warn('🚫 Sprint cart store limitation detected:', error.message);
        // Don't show alert here, let the component handle it
        return { shouldHandleError: true, error };
      } else if (error.message === "Cart data is required") {
        // Handle empty cart data error gracefully
        console.warn('🛒 Cart data is required - treating as empty cart');
        return {
          success: true,
          data: {
            message: 'Cart is empty',
            items_count: 0,
            cart_type: cartType.toLowerCase()
          }
        };
      } else {
        // Log other errors but don't crash the app
        console.error('💥 Cart error:', error.message);
        // Return graceful fallback for other errors too
        return {
          success: false,
          error: error.message,
          cart_type: cartType.toLowerCase()
        };
      }
    }

    // Fallback: log error but don't throw - return graceful response
    console.warn(`⚠️ Unhandled ${cartType} cart error - using graceful fallback`);
    return {
      success: false,
      error: error.message || 'Cart operation failed',
      cart_type: cartType.toLowerCase()
    };
  }
};

// Debounced API call for Phase 2 cart
const debouncedUpdatePhase2Cart = (dispatch, getState) => {
  // Clear any existing timeout
  if (phase2ApiCallTimeout) {
    clearTimeout(phase2ApiCallTimeout);
  }

  // Set new timeout
  phase2ApiCallTimeout = setTimeout(() => {
    dispatch(updatePhase2CartProductInternal());
    phase2ApiCallTimeout = null;
  }, API_DEBOUNCE_DELAY);
};

// Debounced API call for Phase 1 cart
const debouncedUpdatePhase1Cart = (dispatch, getState) => {
  // Clear any existing timeout
  if (phase1ApiCallTimeout) {
    clearTimeout(phase1ApiCallTimeout);
  }

  // Set new timeout
  phase1ApiCallTimeout = setTimeout(() => {
    dispatch(updateCartProductInternal());
    phase1ApiCallTimeout = null;
  }, API_DEBOUNCE_DELAY);
};

export const loadCart = (products) => ({
  type: LOAD_CART,
  payload: products,
});

/** Wipe Redux cart slices and all cart localStorage keys. */
export const clearAllCarts = () => (dispatch) => {
  clearCartStorage();
  dispatch({ type: LOAD_CART, payload: [] });
  dispatch({ type: LOAD_PHASE2_CART, payload: [] });
  dispatch(updateCart([]));
};

const syncEmptyCartToServer = (authToken) =>
  Promise.all([
    makeCartApiCall({ cart_type: "sprint", cart: [] }, authToken).catch(() => null),
    makeCartApiCall({ cart_type: "relay", cart: [] }, authToken).catch(() => null),
  ]);

/** Logout: clear server cart (if logged in) then local + Redux. */
export const clearAllCartsOnLogout = (options = {}) => (dispatch, getState) => {
  const finish = () => dispatch(clearAllCarts());

  // After account delete the token is already revoked — skip server sync to avoid 401.
  if (options.skipServerSync) {
    finish();
    return Promise.resolve();
  }

  const user = getState().user.user;
  const token = user && user.data && user.data.auth_token;
  if (!token) {
    finish();
    return Promise.resolve();
  }
  return syncEmptyCartToServer(token).finally(finish);
};

/** Restore guest cart from localStorage after refresh (cart is not redux-persisted). */
export const hydrateCartFromLocalStorage = () => (dispatch, getState) => {
  const { products, phase2Products } = getState().cart;

  if (!products || products.length === 0) {
    let sprintRaw = localStorage.getItem("savedCartItems_sprint");
    if (!sprintRaw) {
      const legacy = localStorage.getItem("savedCartItems");
      if (legacy) {
        localStorage.setItem("savedCartItems_sprint", legacy);
        localStorage.removeItem("savedCartItems");
        sprintRaw = legacy;
      }
    }
    if (sprintRaw) {
      try {
        const parsed = JSON.parse(sprintRaw);
        if (Array.isArray(parsed) && parsed.length > 0) {
          dispatch(loadCart(parsed));
          dispatch(updateCart(parsed));
        }
      } catch (e) {
        console.error("hydrateCartFromLocalStorage (sprint):", e);
      }
    }
  }

  if (!phase2Products || phase2Products.length === 0) {
    const relayRaw = localStorage.getItem("savedCartItems_relay");
    if (relayRaw) {
      try {
        const parsed = JSON.parse(relayRaw);
        if (Array.isArray(parsed) && parsed.length > 0) {
          dispatch({ type: LOAD_PHASE2_CART, payload: parsed });
        }
      } catch (e) {
        console.error("hydrateCartFromLocalStorage (relay):", e);
      }
    }
  }
};

/**
 * Pull cart from users.sprint_cart_items / relay_cart_items when local Redux
 * (and preferably localStorage) is empty — e.g. new device after login.
 */
export const hydrateCartFromBackend = () => (dispatch, getState) => {
  const state = getState();
  const user = state.user && state.user.user;
  const token = user && user.data && user.data.auth_token;
  if (!token) {
    return Promise.resolve({ success: false, reason: "no_auth" });
  }

  const { products, phase2Products } = state.cart;
  const needSprint = !products || products.length === 0;
  const needRelay = !phase2Products || phase2Products.length === 0;
  if (!needSprint && !needRelay) {
    return Promise.resolve({ success: true, skipped: true });
  }

  const fetchType = (cartType) =>
    Axios.post(
      GET_CART_ITEMS_URL,
      { cart_type: cartType },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    )
      .then((res) => {
        const items = (res.data && res.data.items) || [];
        return Array.isArray(items) ? items : [];
      })
      .catch((err) => {
        console.warn(`hydrateCartFromBackend (${cartType}):`, err.message || err);
        return [];
      });

  return Promise.all([
    needSprint ? fetchType("sprint") : Promise.resolve(null),
    needRelay ? fetchType("relay") : Promise.resolve(null),
  ]).then(([sprintItems, relayItems]) => {
    if (sprintItems && sprintItems.length > 0) {
      dispatch(loadCart(sprintItems));
      dispatch(updateCart(sprintItems));
      localStorage.setItem("savedCartItems_sprint", JSON.stringify(sprintItems));
    }
    if (relayItems && relayItems.length > 0) {
      dispatch({ type: LOAD_PHASE2_CART, payload: relayItems });
      localStorage.setItem("savedCartItems_relay", JSON.stringify(relayItems));
    }
    return { success: true };
  });
};

export const addProduct = (product) => (dispatch, getState) => {
  // Determine if this is a Phase 1 or Phase 2 addition for validation
  const isPhase2 = product.cart_type === 'phase2';
  const cartType = isPhase2 ? CartType.RELAY : CartType.SPRINT;
  const itemMode = product.mode || 'both'; // Default to 'both' if mode not set

  if (!isItemCompatibleWithCart(itemMode, cartType)) {
    const errorMessage = `This item is only available for ${itemMode === 'relay' ? 'Relay' : 'Sprint'} delivery mode.`;
    console.warn('Cannot add item to cart:', errorMessage);
    return Promise.reject({
      type: 'MODE_MISMATCH',
      message: errorMessage,
      itemMode: itemMode,
      cartType: cartType
    });
  }

  // Ensure the dispatched product always carries a numeric quantity. Without
  // this, downstream consumers (FloatCart push, +/- counters, badges) end up
  // with `quantity: undefined`, which shows nothing in the UI and breaks
  // subsequent +/- math (`undefined + 1 = NaN`).
  const productWithQty = {
    ...product,
    quantity: product.quantity || 1,
  };

  return dispatch({ type: ADD_PRODUCT, payload: productWithQty });
};

export const addItemToCart = (product) => (dispatch, getState) => {
  // Use the same validation as addProduct
  return dispatch(addProduct(product));
};

export const removeProduct = (product) => (dispatch) => {
  return dispatch({ type: REMOVE_PRODUCT, payload: product });
};

// Phase 2 Cart Actions
export const loadPhase2Cart = (products) => ({
  type: LOAD_PHASE2_CART,
  payload: products,
});

export const addPhase2Product = (product) => (dispatch, getState) => {
  // Validate item mode matches relay cart type
  const cartType = CartType.RELAY; // Relay cart
  const itemMode = product.mode || 'both'; // Default to 'both' if mode not set

  if (!isItemCompatibleWithCart(itemMode, cartType)) {
    const errorMessage = `This item is only available for ${itemMode === 'sprint' ? 'Sprint' : 'Relay'} delivery mode.`;
    console.warn('Cannot add item to cart:', errorMessage);
    return Promise.reject({
      type: 'MODE_MISMATCH',
      message: errorMessage,
      itemMode: itemMode,
      cartType: cartType
    });
  }

  // Get current Phase 2 cart products
  const currentPhase2Products = getState().cart.phase2Products || [];

  // Check if product already exists in cart
  let productAlreadyInCart = false;
  const updatedProducts = [...currentPhase2Products];

  updatedProducts.forEach((cp) => {
    if (cp.id === product.id) {
      if (areCartAddonsEqual(cp.selectedaddons, product.selectedaddons)) {
        cp.quantity += 1;
        productAlreadyInCart = true;
      }
    }
  });

  // If product not in cart, add it
  if (!productAlreadyInCart) {
    const newProduct = {
      ...product,
      quantity: 1,
      cart_type: 'phase2',
      selectedaddons: product.selectedaddons || []
    };
    updatedProducts.push(newProduct);
  }


  // Update localStorage
  localStorage.setItem('savedCartItems_relay', JSON.stringify(updatedProducts));

  // Dispatch action to update cart state
  dispatch({
    type: UPDATE_PHASE2_CART,
    payload: updatedProducts,
  });

  // Use debounced API call to prevent multiple calls
  debouncedUpdatePhase2Cart(dispatch, getState);

  return Promise.resolve(updatedProducts);
};

export const removePhase2Product = (product) => (dispatch, getState) => {
  // Get current Phase 2 cart products
  const currentPhase2Products = getState().cart.phase2Products || [];
  const updatedProducts = [...currentPhase2Products];

  // Find the specific item index matching BOTH id AND selected addons
  const index = updatedProducts.findIndex((p) => {
    return (
      p.id === product.id && areCartAddonsEqual(p.selectedaddons, product.selectedaddons)
    );
  });

  if (index >= 0) {
    const item = updatedProducts[index];
    if (item.quantity === 1) {
      // Remove product from cart if quantity is 1
      updatedProducts.splice(index, 1);
    } else {
      // Decrease quantity by 1
      // Create a new object to avoid direct mutation of the state object (though we are already mutating the array clone)
      updatedProducts[index] = { ...item, quantity: item.quantity - 1 };
    }

    // Update localStorage
    localStorage.setItem('savedCartItems_relay', JSON.stringify(updatedProducts));

    // Dispatch action to update cart state
    dispatch({
      type: UPDATE_PHASE2_CART,
      payload: updatedProducts,
    });

    // Use debounced API call to prevent multiple calls
    debouncedUpdatePhase2Cart(dispatch, getState);
  }

  return Promise.resolve(updatedProducts);
};

export const updatePhase2Cart = (products) => ({
  type: UPDATE_PHASE2_CART,
  payload: products,
});

export const clearPhase2Cart = () => ({
  type: CLEAR_PHASE2_CART,
});

// Internal function for Phase 2 cart API call (not debounced)
const updatePhase2CartProductInternal = () => (dispatch, getState) => {
  let cartItems = [];
  const stateData = getState().cart.phase2Products || [];

  if (stateData.length) {
    stateData.forEach((eachItem) => {
      const serialized = serializeCartItemForApi(eachItem);
      if (serialized) {
        cartItems.push(serialized);
      } else {
        console.warn('🚫 Skipping invalid Phase 2 cart item (missing required fields):', eachItem);
      }
    });
  }

  const user = getState().user.user;

  if (user && user.data) {
    // Always make API call to sync cart state with server (even when empty)
    const payload = {
      cart_type: "relay", // Phase 2 is Relay
      cart: cartItems, // Send even if empty to properly clear server cart
    };

    return makeCartApiCall(payload, user.data.auth_token)
      .then((result) => {
        return handleCartResponse(result, 'Relay');
      })
      .catch((error) => {
        console.error("Phase 2 (Relay) cart update failed:", error);
        // Return graceful fallback instead of crashing
        return {
          success: false,
          offline: true,
          message: 'Cart saved locally - will sync when connection is restored',
          cart_type: 'relay'
        };
      });
  }
};

// Public function for Phase 2 cart update (with debouncing)
export const updatePhase2CartProduct = () => (dispatch, getState) => {
  return new Promise((resolve, reject) => {
    // Clear any existing timeout
    if (phase2ApiCallTimeout) {
      clearTimeout(phase2ApiCallTimeout);
    }

    // Set new timeout
    phase2ApiCallTimeout = setTimeout(() => {

      // Call the internal function that returns a Promise
      const result = dispatch(updatePhase2CartProductInternal());

      if (result && typeof result.then === 'function') {
        // If it returns a Promise, handle it
        result
          .then((response) => {
            phase2ApiCallTimeout = null;
            resolve(response);
          })
          .catch((error) => {
            phase2ApiCallTimeout = null;
            reject(error);
          });
      } else {
        // If no Promise returned (e.g., no user), resolve immediately
        phase2ApiCallTimeout = null;
        resolve({ success: true, message: 'Cart updated locally' });
      }
    }, API_DEBOUNCE_DELAY);
  });
};

// Internal function for Phase 1 cart API call (not debounced)
const updateCartProductInternal = () => (dispatch, getState) => {
  let cartItems = [];
  const stateData = getState().cart.products;

  if (stateData.length) {
    stateData.forEach((eachItem) => {
      const serialized = serializeCartItemForApi(eachItem);
      if (serialized) {
        cartItems.push(serialized);
      } else {
        console.warn('🚫 Skipping invalid Phase 1 cart item (missing required fields):', eachItem);
      }
    });
  }

  const user = getState().user.user;

  if (user && user.data) {
    // Always make API call to sync cart state with server (even when empty)
    const payload = {
      cart_type: "sprint", // Phase 1 is Sprint
      cart: cartItems, // Send even if empty to properly clear server cart
    };

    return makeCartApiCall(payload, user.data.auth_token)
      .then((result) => {
        return handleCartResponse(result, 'Sprint');
      })
      .catch((error) => {
        console.error("Phase 1 (Sprint) cart update failed:", error);

        // Handle Sprint cart store limitation error specifically
        if (error.response && error.response.data && error.response.data.error === "Sprint cart store limitation") {
          // Dispatch an action to handle this error in the UI
          dispatch({
            type: CART_STORE_LIMITATION_ERROR,
            payload: {
              error: error.response.data.error,
              message: error.response.data.message,
              cartItems: cartItems
            }
          });
        }

        // Return graceful fallback instead of crashing
        return {
          success: false,
          offline: true,
          message: 'Cart saved locally - will sync when connection is restored',
          cart_type: 'sprint'
        };
      });
  }
};

// Public function for Phase 1 cart update (with debouncing)
export const updateCartProduct = () => (dispatch, getState) => {
  return new Promise((resolve, reject) => {
    // Clear any existing timeout
    if (phase1ApiCallTimeout) {
      clearTimeout(phase1ApiCallTimeout);
    }

    // Set new timeout
    phase1ApiCallTimeout = setTimeout(() => {

      // Call the internal function that returns a Promise
      const result = dispatch(updateCartProductInternal());

      if (result && typeof result.then === 'function') {
        // If it returns a Promise, handle it
        result
          .then((response) => {
            phase1ApiCallTimeout = null;
            resolve(response);
          })
          .catch((error) => {
            phase1ApiCallTimeout = null;
            reject(error);
          });
      } else {
        // If no Promise returned (e.g., no user), resolve immediately
        phase1ApiCallTimeout = null;
        resolve({ success: true, message: 'Cart updated locally' });
      }
    }, API_DEBOUNCE_DELAY);
  });
};

export const setDeliveryDate = (date) => (dispatch) => {
  return dispatch({ type: DELIVERY_DATE, payload: date });
};

export const setDeliveryTimeSlot = (slot) => (dispatch) => {
  return dispatch({ type: DELIVERY_TIME_SLOT, payload: slot });
};

// Clear cart errors
export const clearCartError = () => ({
  type: CLEAR_CART_ERROR,
});

// Clear Sprint cart and add new item (for store limitation handling)
export const clearSprintCartAndAddItem = (newItem) => (dispatch, getState) => {
  const user = getState().user.user;

  if (user && user.data) {
    // First clear the cart
    const clearPayload = {
      cart_type: "sprint",
      cart: [],
    };

    return makeCartApiCall(clearPayload, user.data.auth_token)
      .then((clearResult) => {
        if (clearResult.success) {
          // Clear local cart
          dispatch({ type: LOAD_CART, payload: [] });

          // Add new item
          const newCartPayload = {
            cart_type: "sprint",
            cart: [{
              id: newItem.id,
              restaurant_id: newItem.restaurant_id,
              item_category_id: newItem.item_category_id,
              name: newItem.name,
              price: newItem.price,
              old_price: newItem.old_price,
              quantity: newItem.quantity || 1,
              zone_id: newItem.zone_id || null,
            }],
          };

          return makeCartApiCall(newCartPayload, user.data.auth_token)
            .then((addResult) => {
              if (addResult.success) {
                dispatch({ type: ADD_PRODUCT, payload: newItem });
                dispatch(clearCartError());
                return addResult.data;
              }
              throw new Error('Failed to add item to cart');
            });
        }
        throw new Error('Failed to clear cart');
      })
      .catch((error) => {
        console.error('Failed to clear cart and add item:', error);
        throw error;
      });
  }

  return Promise.reject(new Error('User not authenticated'));
}; 